import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { extractTicketQrToken } from '@/lib/ticketQr';

// Client hooks for the admin ticket-admission desk. Both call admin/service-gated
// Edge Functions; the customer-app `admin` role reaches this UI and faculty/service
// reach the check-in endpoint directly. The check-in function encodes the outcome
// in the response `state` field across several HTTP status codes (200 accepted,
// 404 not_found, 409 already_checked_in/wrong_show, 422 refunded/void), so we read
// the body on both success and error responses.

export type CheckinState =
  | 'accepted'
  | 'already_checked_in'
  | 'wrong_show'
  | 'not_found'
  | 'refunded'
  | 'void'
  | 'malformed'
  | 'unauthorized'
  | 'server_error';

export interface CheckinTicketDetail {
  ticket_id: string;
  family_name: string | null;
  show_name: string | null;
  performance_at: string | null;
  seat_label: string | null;
}

export interface CheckinOutcome {
  ok: boolean;
  state: CheckinState;
  error: string | null;
  ticket_id?: string | null;
  checked_in_at?: string | null;
  ticket?: CheckinTicketDetail | null;
}

export interface ReissueOutcome {
  ok: boolean;
  error: string | null;
  ticket_id?: string | null;
  reissued_at?: string | null;
}

// `supabase.functions.invoke` returns the parsed body in `data` for 2xx and, for
// non-2xx, a FunctionsHttpError whose `context` is the raw Response. Read either.
async function readFunctionBody<T>(data: unknown, error: unknown): Promise<T | null> {
  if (data && typeof data === 'object') return data as T;
  const context = (error as { context?: Response } | null)?.context;
  if (context && typeof context.json === 'function') {
    return (await context.json().catch(() => null)) as T | null;
  }
  return null;
}

export function useCheckinTicket() {
  return useMutation({
    mutationFn: async (input: {
      qrPayload: string;
      showId?: string | null;
    }): Promise<CheckinOutcome> => {
      // Validate client-side first so an obviously-bad scan never hits the network.
      const token = extractTicketQrToken(input.qrPayload);
      if (!token) {
        return { ok: false, state: 'malformed', error: 'malformed' };
      }
      const { data, error } = (await supabase.functions.invoke<CheckinOutcome>('ticket-checkin', {
        body: { qrPayload: token, showId: input.showId ?? undefined },
      })) as { data: CheckinOutcome | null; error: unknown };
      const body = await readFunctionBody<CheckinOutcome>(data, error);
      if (!body || typeof body.state !== 'string') {
        throw new Error('Check-in request failed. Please try again.');
      }
      return body;
    },
  });
}

export function useReissueTicketQr() {
  return useMutation({
    mutationFn: async (input: { ticketId: string; reason?: string }): Promise<ReissueOutcome> => {
      const trimmedReason = input.reason?.trim();
      const { data, error } = (await supabase.functions.invoke<ReissueOutcome>(
        'ticket-qr-reissue',
        {
          body: { ticketId: input.ticketId, reason: trimmedReason },
        },
      )) as { data: ReissueOutcome | null; error: unknown };
      const body = await readFunctionBody<ReissueOutcome>(data, error);
      if (!body || typeof body.ok !== 'boolean') {
        throw new Error('Reissue request failed. Please try again.');
      }
      return body;
    },
  });
}

const CHECKIN_COPY: Record<CheckinState, { tone: 'success' | 'warn' | 'danger'; label: string }> = {
  accepted: { tone: 'success', label: 'Admitted' },
  already_checked_in: { tone: 'warn', label: 'Already checked in' },
  wrong_show: { tone: 'warn', label: 'Wrong show for this entrance' },
  not_found: { tone: 'danger', label: 'Ticket not found' },
  refunded: { tone: 'danger', label: 'Ticket was refunded' },
  void: { tone: 'danger', label: 'Ticket is void' },
  malformed: { tone: 'danger', label: 'Not a valid ticket code' },
  unauthorized: { tone: 'danger', label: 'Not authorized to check in' },
  server_error: { tone: 'danger', label: 'Something went wrong — try again' },
};

export function checkinCopy(state: CheckinState): {
  tone: 'success' | 'warn' | 'danger';
  label: string;
} {
  return CHECKIN_COPY[state] ?? CHECKIN_COPY.server_error;
}
